import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component for dynamic page-level meta tags
 * Optimized for Virtual Try-On, AI Shopping, and Fashion E-commerce keywords
 */

const SEO = ({
  title,
  description,
  keywords = [],
  canonicalUrl,
  ogImage = 'https://nanokart.ai/og-image.png',
  ogType = 'website',
  noIndex = false,
  structuredData = null,
  article = null
}) => {
  // Base keywords for all pages
  const baseKeywords = [
    'conversion optimization fashion',
    'reduce fashion returns',
    'fashion brand ROI',
    'virtual try-on',
    'AI try-on',
    'virtual fitting room',
    'online try-on',
    'size recommendation AI',
    'return rate reduction',
    'fashion ecommerce',
    'try before you buy',
    'AI fashion technology',
    'virtual dressing room',
    'NanoKart'
  ];

  // Merge page-specific keywords with base keywords
  const allKeywords = [...new Set([...keywords, ...baseKeywords])].join(', ');

  // Default title suffix
  const fullTitle = title 
    ? `${title} | NanoKart.ai - Boost Conversions & Cut Returns`
    : 'NanoKart.ai - Conversion & Return Optimization for Fashion Brands';

  // Default description
  const metaDescription = description || 
    'NanoKart.ai is the conversion & return optimization engine for fashion brands. AI-powered virtual try-on and smart size recommendations. Boost conversions by 35%, cut returns by 30%.';

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={allKeywords} />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="NanoKart.ai" />
      <meta property="og:locale" content="en_IN" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:creator" content="@nanokartai" />
      
      {/* Article specific (for blog posts etc) */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:modified_time" content={article.modifiedTime} />
          <meta property="article:author" content={article.author || 'NanoKart Team'} />
          <meta property="article:section" content={article.section || 'Technology'} />
        </>
      )}
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// Pre-defined SEO configurations for common pages
export const SEO_CONFIGS = {
  home: {
    title: 'Sell More. Return Less. | Conversion & Return Optimization for Fashion',
    description: 'NanoKart.ai is the conversion & return optimization engine for fashion brands. AI-powered virtual try-on and smart size recommendations. Boost conversions by 35%, cut returns by 30%.',
    keywords: ['fashion conversion optimization', 'reduce fashion returns India', 'virtual try-on India', 'AI virtual fitting room', 'fashion ecommerce ROI', 'online try-on platform', 'AI fashion technology'],
    canonicalUrl: 'https://nanokart.ai/'
  },
  tryon: {
    title: 'Virtual Try-On API - See Clothes Before You Buy',
    description: 'Experience AI-powered virtual try-on technology. Upload your photo and any garment to see how it looks on you instantly. Perfect for fashion e-commerce, reduce returns, increase confidence.',
    keywords: ['virtual try-on API', 'AI clothing try-on', 'virtual fitting room API', 'see clothes on me', 'online try-on solution', 'fashion visualization'],
    canonicalUrl: 'https://nanokart.ai/tryon',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "NanoKart Virtual Try-On",
      "applicationCategory": "ShoppingApplication",
      "description": "AI-powered virtual try-on that lets you see how clothes will look on you before purchasing.",
      "operatingSystem": "Web Browser",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "INR"
      }
    }
  },
  moments: {
    title: 'AI Lifestyle Moments - Visualize Fashion in Real Scenarios',
    description: 'See how outfits look in different life moments - at work, travel, parties, and celebrations. AI-powered lifestyle visualization for fashion e-commerce.',
    keywords: ['lifestyle fashion visualization', 'AI moment generation', 'fashion in context', 'outfit visualization', 'style moments'],
    canonicalUrl: 'https://nanokart.ai/moments'
  },
  pricing: {
    title: 'Pricing Plans - Virtual Try-On API for Every Business',
    description: 'Affordable virtual try-on API pricing. Start free with 100 credits. Plans for startups to enterprises. Pay-as-you-go or monthly subscriptions.',
    keywords: ['virtual try-on pricing', 'API pricing', 'fashion tech pricing', 'affordable try-on solution'],
    canonicalUrl: 'https://nanokart.ai/pricing',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "NanoKart.ai Virtual Try-On API",
      "description": "AI-powered virtual try-on API for fashion e-commerce",
      "brand": { "@type": "Brand", "name": "NanoKart.ai" },
      "offers": {
        "@type": "AggregateOffer",
        "lowPrice": "0",
        "highPrice": "4999",
        "priceCurrency": "INR",
        "offerCount": "4"
      }
    }
  },
  docs: {
    title: 'API Documentation - Integrate Virtual Try-On',
    description: 'Complete API documentation for NanoKart.ai virtual try-on integration. RESTful API, webhooks, SDKs, and code examples for quick integration.',
    keywords: ['virtual try-on API docs', 'fashion API documentation', 'try-on integration guide', 'e-commerce API'],
    canonicalUrl: 'https://nanokart.ai/docs'
  },
  whyNanokart: {
    title: 'Why Nanokart | Conversion & Return Optimization for Fashion Brands',
    description: 'See how Nanokart helps fashion brands boost conversions and slash returns with AI-powered virtual try-on and smart size recommendations. Real outcomes, measurable ROI.',
    keywords: ['conversion optimization fashion', 'reduce fashion returns', 'virtual try-on for brands', 'AI virtual fitting room', 'size recommendation API', 'fashion brand ROI', 'return rate reduction ecommerce'],
    canonicalUrl: 'https://nanokart.ai/why-nanokart'
  },
  contact: {
    title: 'Contact Us - Get in Touch',
    description: 'Contact NanoKart.ai for virtual try-on solutions, enterprise inquiries, or support. We\'re here to help transform your e-commerce experience.',
    keywords: ['contact NanoKart', 'virtual try-on support', 'fashion tech contact'],
    canonicalUrl: 'https://nanokart.ai/contact'
  },
  register: {
    title: 'Get Started Free - Create Your Account',
    description: 'Start using NanoKart.ai virtual try-on for free. Get 100 credits instantly. No credit card required. Transform your fashion e-commerce today.',
    keywords: ['start virtual try-on', 'free try-on API', 'sign up fashion tech'],
    canonicalUrl: 'https://nanokart.ai/partners/register',
    noIndex: true
  },
  login: {
    title: 'Partner Login - Access Your Dashboard',
    description: 'Login to your NanoKart.ai partner dashboard. Manage your virtual try-on integrations, analytics, and settings.',
    keywords: ['partner login', 'dashboard access'],
    canonicalUrl: 'https://nanokart.ai/partners/login',
    noIndex: true
  },
  requestAccess: {
    title: 'Request Partner Access - Join NanoKart.ai',
    description: 'Apply for NanoKart.ai partner access. Get early access to AI-powered virtual try-on APIs for your fashion e-commerce business.',
    keywords: ['partner access', 'virtual try-on API access', 'fashion tech partnership'],
    canonicalUrl: 'https://nanokart.ai/request-access'
  },
  terms: {
    title: 'Terms & Conditions - NanoKart.ai',
    description: 'Read NanoKart.ai terms of service. Understand the terms governing use of our virtual try-on APIs and partner services.',
    keywords: ['terms of service', 'API terms', 'service agreement'],
    canonicalUrl: 'https://nanokart.ai/terms'
  },
  privacy: {
    title: 'Privacy Policy - NanoKart.ai',
    description: 'NanoKart.ai privacy policy. Learn how we protect your data and comply with Indian data protection laws including DPDP Act 2023.',
    keywords: ['privacy policy', 'data protection', 'DPDP compliance'],
    canonicalUrl: 'https://nanokart.ai/privacy'
  },
  refunds: {
    title: 'Refund Policy - NanoKart.ai',
    description: 'NanoKart.ai refund policy for credits and subscriptions. Understand our fair refund terms for API usage and subscription plans.',
    keywords: ['refund policy', 'credit refunds', 'subscription cancellation'],
    canonicalUrl: 'https://nanokart.ai/refunds'
  },
  // Token pages - always noindex
  publicTryOn: {
    title: 'Virtual Try-On Experience',
    description: 'Try on this garment virtually using NanoKart.ai AI technology.',
    noIndex: true
  },
  sharedVTO: {
    title: 'Shared Virtual Try-On',
    description: 'View this shared virtual try-on result.',
    noIndex: true
  },
  // Form submission success states - noindex
  contactSubmitted: {
    title: 'Message Sent - Contact Us',
    description: 'Thank you for contacting NanoKart.ai.',
    noIndex: true
  },
  requestAccessSubmitted: {
    title: 'Application Submitted - Request Access',
    description: 'Thank you for your interest in NanoKart.ai.',
    noIndex: true
  }
};

export default SEO;
